import * as React from "react";
import * as Constants from "@/shared/constants";
import type {FieldError, FixedBreak} from "@/shared/timr";
import {FixedBreakType} from "@/shared/timrTypes";
import {
	formatFixedBreakErrorSummaryMessage,
	getFixedBreakRangeFields
} from "@/features/admin/breakModel/breakModelDialogHelper";
import {
	EMPTY_FIXED_BREAK,
	FIXED_BREAK_FIELDS,
	type FixedBreakConfiguration,
	type FixedBreakField,
	type FixedBreakKey
} from "@/features/admin/breakModel/fixedBreakConfigurationModel";
import {FixedBreakTypeSelector} from "@/features/admin/breakModel/fixedBreakTypeSelector";
import {FixedBreakAllDaysEqualConfiguration} from "@/features/admin/breakModel/fixedBreakAllDaysEqualConfiguration";
import {FixedBreakPerDayConfiguration} from "@/features/admin/breakModel/fixedBreakPerDayConfiguration";

export type {FixedBreakConfiguration};

interface FixedBreakConfigurationPanelProps {
	/** Complete fixed-break configuration currently stored on the dialog's break model. */
	configuration: FixedBreakConfiguration;
	/** Server-side validation errors that belong to fixed-break fields. */
	fieldErrors: FieldError[];
	/** Emits a normalized configuration whenever the user changes a fixed-break value or mode. */
	onChange: (configuration: FixedBreakConfiguration) => void;
}

function cloneFixedBreak(fixedBreak: FixedBreak | null): FixedBreak {
	return {...EMPTY_FIXED_BREAK, ...fixedBreak};
}

function cloneConfiguration(configuration: FixedBreakConfiguration): FixedBreakConfiguration {
	return {
		fixedBreakType: configuration.fixedBreakType ?? FixedBreakType.NONE,
		fixedBreak: cloneFixedBreak(configuration.fixedBreak),
		fixedBreakMonday: cloneFixedBreak(configuration.fixedBreakMonday),
		fixedBreakTuesday: cloneFixedBreak(configuration.fixedBreakTuesday),
		fixedBreakWednesday: cloneFixedBreak(configuration.fixedBreakWednesday),
		fixedBreakThursday: cloneFixedBreak(configuration.fixedBreakThursday),
		fixedBreakFriday: cloneFixedBreak(configuration.fixedBreakFriday),
		fixedBreakSaturday: cloneFixedBreak(configuration.fixedBreakSaturday),
		fixedBreakSunday: cloneFixedBreak(configuration.fixedBreakSunday)
	};
}

/**
 * Owns the fixed-break editor UI inside the legacy break-model dialog.
 *
 * The panel supports the three backend modes: disabled, one shared set of fixed breaks
 * for all days, or separate fixed breaks per weekday. It normalizes nullable backend
 * structures before rendering so child components can work with complete `FixedBreak` objects.
 */
export function FixedBreakConfigurationPanel({configuration, fieldErrors, onChange}: Readonly<FixedBreakConfigurationPanelProps>) {
	const normalizedConfiguration = cloneConfiguration(configuration);
	const errorFields = new Set(fieldErrors.flatMap(fieldError => getFixedBreakRangeFields(fieldError.field)));

	const updateConfiguration = (mutator: (next: FixedBreakConfiguration) => void) => {
		const next = cloneConfiguration(normalizedConfiguration);
		mutator(next);
		onChange(next);
	};

	// switch the mode and, when moving to PER_DAY, prefill empty day configs from the shared break
	const setFixedBreakType = (fixedBreakType: FixedBreakType) => {
		updateConfiguration(updatedConfiguration => {
			const previousType = updatedConfiguration.fixedBreakType;
			updatedConfiguration.fixedBreakType = fixedBreakType;

			const isFixedBreakEmpty = (fixedBreak: FixedBreak) =>
				FIXED_BREAK_FIELDS.every(([fromField, toField]) => !fixedBreak[fromField] && !fixedBreak[toField]);

			const isPerDayConfigurationEmpty = (configuration: FixedBreakConfiguration) =>
				Constants.WEEKDAYS.every(day => isFixedBreakEmpty(configuration[`fixedBreak${day}`]));

			// when switching from ALL_DAYS_EQUAL to PER_DAY, seed empty day configs with the shared values so the user can edit them day by day
			if (previousType === FixedBreakType.ALL_DAYS_EQUAL
				&& fixedBreakType === FixedBreakType.PER_DAY
				&& isPerDayConfigurationEmpty(updatedConfiguration)) {
				Constants.WEEKDAYS.forEach(day => {
					updatedConfiguration[`fixedBreak${day}`] = cloneFixedBreak(updatedConfiguration.fixedBreak);
				});
			}
		});
	};

	// update one break field and use Monday as the template for still-empty days in PER_DAY mode
	const setFixedBreakValue = (fixedBreakKey: FixedBreakKey, field: FixedBreakField, value: string | null) => {
		updateConfiguration(updatedConfiguration => {
			updatedConfiguration[fixedBreakKey][field] = value;

			if (fixedBreakKey === "fixedBreakMonday") {
				// When Monday is edited (per-day mode), copy its value to the other days that are still empty.
				const otherDays = Constants.WEEKDAYS.filter(day => day !== "Monday");
				if (otherDays.every(day => !updatedConfiguration[`fixedBreak${day}`][field])) {
					otherDays.forEach(day => {
						updatedConfiguration[`fixedBreak${day}`][field] = value;
					});
				}
			}
		});
	};

	// clear one break range completely when a user disables it via checkbox unselect
	const clearFixedBreakRange = (fixedBreakKey: FixedBreakKey, fromField: FixedBreakField, toField: FixedBreakField) => {
		updateConfiguration(updatedConfiguration => {
			updatedConfiguration[fixedBreakKey][fromField] = null;
			updatedConfiguration[fixedBreakKey][toField] = null;
		});
	};

	return (
		<div className="fixed-break-configuration-panel">
			<FixedBreakTypeSelector value={normalizedConfiguration.fixedBreakType} onChange={setFixedBreakType}/>

			{normalizedConfiguration.fixedBreakType === FixedBreakType.ALL_DAYS_EQUAL && (
				<div className="fixed-break-configuration-content">
					<FixedBreakAllDaysEqualConfiguration
						fixedBreakKey="fixedBreak"
						fixedBreak={normalizedConfiguration.fixedBreak}
						errorFields={errorFields}
						onChange={setFixedBreakValue}
						onClearRange={clearFixedBreakRange}
					/>
					<FixedBreakValidationErrors fieldErrors={fieldErrors}/>
				</div>
			)}

			{normalizedConfiguration.fixedBreakType === FixedBreakType.PER_DAY && (
				<div className="fixed-break-configuration-content fixed-break-configuration-content-per-day">
					<FixedBreakPerDayConfiguration
						configuration={normalizedConfiguration}
						errorFields={errorFields}
						onChange={setFixedBreakValue}
					/>
					<FixedBreakValidationErrors fieldErrors={fieldErrors}/>
				</div>
			)}
		</div>
	);
}

interface FixedBreakValidationErrorsProps {
	/** Fixed-break validation errors returned by the backend. */
	fieldErrors: FieldError[];
}

/**
 * Renders a deduplicated validation summary for fixed-break errors.
 *
 * Fixed-break inputs are too compact for inline messages, so the React panel shows
 * field highlighting beside a summary that includes the weekday and break number.
 */
function FixedBreakValidationErrors({fieldErrors}: Readonly<FixedBreakValidationErrorsProps>) {
	const uniqueMessages = Array.from(new Set(fieldErrors
		.map(fieldError => formatFixedBreakErrorSummaryMessage(fieldError))
		.filter((message): message is string => Boolean(message))));

	if (uniqueMessages.length === 0) {
		return null;
	}

	return (
		<div className="alert alert-danger fixed-break-validation-summary mt-10 mb-20">
			<div className="text-semibold mb-5">{messages["dialog.validationerrors.title"]}</div>
			<ul className="mb-0">
				{uniqueMessages.map(message => <li key={message}>{message}</li>)}
			</ul>
		</div>
	);
}
